import {
  supabase,
} from "../supabaseClient";

import {
  API_BASE_URL,
} from "../config/apiBase";


// =====================================================
// CAREER RESILIENCE SCORE API
//
// IMPORTANT:
//
// Career Resilience Score:
//   May be loaded from the backend/database.
//
// Financial Runway Calculator:
//   Must remain PRIVATE and LOCAL ONLY.
//   Financial inputs/results are NOT sent to the API.
//   Financial inputs/results are NOT stored in Supabase.
// =====================================================


// =====================================================
// GET SUPABASE ACCESS TOKEN
// =====================================================

async function getAccessToken() {
  const {
    data,
    error,
  } =
    await supabase.auth.getSession();

  if (error) {
    console.error(
      "[Career Resilience] Supabase session error:",
      error
    );

    throw new Error(
      "Unable to verify your login session."
    );
  }

  const token =
    data?.session?.access_token;

  if (!token) {
    throw new Error(
      "Your session has expired. Please login again."
    );
  }

  return token;
}


// =====================================================
// SAFE RESPONSE READER
// =====================================================

async function readResponseBody(
  response
) {
  const contentType =
    response.headers.get(
      "content-type"
    ) || "";

  if (
    contentType.includes(
      "application/json"
    ) ||
    contentType.includes(
      "problem+json"
    )
  ) {
    return await response
      .json()
      .catch(
        () => null
      );
  }

  const text =
    await response
      .text()
      .catch(
        () => ""
      );

  return text
    ? {
        detail:
          text,
      }
    : null;
}


// =====================================================
// COMMON API ERROR HANDLER
// =====================================================

function throwApiError(
  response,
  body,
  fallbackMessage
) {
  if (
    response.status === 401
  ) {
    throw new Error(
      "Your login session is no longer valid. Please login again."
    );
  }

  if (
    response.status === 403
  ) {
    throw new Error(
      "You are not authorized to perform this action."
    );
  }

  if (
    response.status === 404
  ) {
    throw new Error(
      "Career Resilience API endpoint was not found."
    );
  }

  if (
    response.status === 429
  ) {
    throw new Error(
      "Too many requests. Please try again later."
    );
  }

  if (
    response.status >= 500
  ) {
    throw new Error(
      body?.detail ||
      body?.message ||
      body?.title ||
      "Career Resilience service is temporarily unavailable."
    );
  }

  throw new Error(
    body?.detail ||
    body?.message ||
    body?.title ||
    `${fallbackMessage} (${response.status})`
  );
}


// =====================================================
// GET CURRENT USER CAREER RESILIENCE SCORE
//
// GET
// /api/career-resilience/my-score
//
// This endpoint is allowed because it loads the user's
// Career Resilience profile.
//
// It has nothing to do with storing Financial Runway
// Calculator inputs.
// =====================================================

export async function getMyCareerResilienceScore() {
  const token =
    await getAccessToken();

  const url =
    `${API_BASE_URL}/api/career-resilience/my-score`;

  console.log(
    "[RCRS] Loading:",
    url
  );

  let response;

  try {
    response =
      await fetch(
        url,
        {
          method:
            "GET",

          headers: {
            Accept:
              "application/json",

            Authorization:
              `Bearer ${token}`,
          },
        }
      );
  }
  catch (
    networkError
  ) {
    console.error(
      "[RCRS] Network error:",
      networkError
    );

    throw new Error(
      "Unable to connect to the Career Resilience service."
    );
  }

  const body =
    await readResponseBody(
      response
    );

  console.log(
    "[RCRS] Response status:",
    response.status
  );

  if (
    !response.ok
  ) {
    console.error(
      "[RCRS] API failure:",
      {
        url,

        status:
          response.status,

        statusText:
          response.statusText,

        body,
      }
    );

    throwApiError(
      response,
      body,
      "Unable to load your Career Resilience Score."
    );
  }

  return body;
}


// =====================================================
// FINANCIAL RUNWAY - LOCAL ONLY
// =====================================================
//
// IMPORTANT:
//
// The old implementation called:
//
// POST
// /api/career-resilience/financial-readiness
//
// That API call has intentionally been REMOVED.
//
// Financial calculator information must NEVER be:
//
// - sent to ASP.NET API
// - stored in Supabase
// - saved to career_resilience_scores
// - stored in localStorage
// - stored in sessionStorage
//
// The result should exist only in React component memory
// while the calculator page is open.
// =====================================================


// =====================================================
// LEGACY COMPATIBILITY FUNCTION
//
// Keep this temporarily because an existing React page may
// still import saveFinancialReadiness().
//
// THIS FUNCTION DOES NOT CALL THE API.
// THIS FUNCTION DOES NOT SAVE ANYTHING.
//
// It simply returns the supplied calculated result in memory.
// =====================================================

export async function saveFinancialReadiness(
  payload
) {
  if (
    !payload ||
    typeof payload !== "object"
  ) {
    throw new Error(
      "Financial Runway calculation data is required."
    );
  }

  console.warn(
    "[Financial Runway] Database saving is disabled. " +
    "Result remains local to this page only."
  );

  // ---------------------------------------------------
  // NO fetch()
  // NO axios()
  // NO Supabase insert/update
  // NO localStorage
  // NO sessionStorage
  // ---------------------------------------------------

  return {
    ...payload,

    saved:
      false,

    storage:
      "memory-only",
  };
}